import { useMutation, useQuery } from '@apollo/client';
import {
  Checkbox,
  Input,
  LabelButton,
  ModalContainer,
  MultiSelectPicker,
  MultiSelectPickerContent,
  MultiSelectPickerItem,
  MultiSelectPickerList,
  MultiSelectPickerTrigger,
  Select,
} from '@teamsnap/snap-ui';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import CreateTemplateMutation from 'core/gql/mutations/create-export-template';
import DeleteTemplateMutation from 'core/gql/mutations/delete-export-template';
import TemplatesQuery from 'core/gql/queries/export-templates';
import * as React from 'react';

type Props = {
  columns: LVMap[];
  title: string;
  show: boolean;
  onClose: () => any;
  onSubmit: (columns: LVMap[], exportType: string, templateName: string) => any;
  organizationId: number;
  exportTypes: LVMap[];
  defaultExportType?: string;
};

type LVMap = {
  label: string;
  value: string;
};

const ExportModal = ({
  title,
  show,
  onClose,
  columns,
  onSubmit,
  organizationId,
  exportTypes,
  defaultExportType,
}: Props) => {
  const [exportType, setExportType] = React.useState<string>(
    defaultExportType || exportTypes[0]?.value || ''
  );
  const [selectedColumns, setSelectedColumns] = React.useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState('manual');
  const [saveTemplate, setSaveTemplate] = React.useState(false);
  const [templateName, setTemplateName] = React.useState('');
  const [templateSubText, setTemplateSubText] = React.useState(undefined);
  const [storedTemplates, setStoredTemplates] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);

  const [createTemplate] = useMutation(CreateTemplateMutation);
  const [deleteTemplate] = useMutation(DeleteTemplateMutation);

  const { data, refetch } = useQuery(TemplatesQuery, {
    variables: { organizationId },
    fetchPolicy: 'cache-first',
  });

  React.useEffect(() => {
    const baseData = data?.exportTemplates || [];
    setStoredTemplates(baseData);
  }, [data]);

  const filterTemplates = (storedTemplates, exportType) =>
    storedTemplates.filter((template) => template.exportType === exportType);

  React.useEffect(() => {
    setTemplates(filterTemplates(storedTemplates, exportType));
  }, [storedTemplates, exportType]);

  const onChangeTemplate = (template) => {
    setSelectedTemplate(template);
    if (template !== 'manual') {
      setTemplateSubText(undefined);
      const newlySelectedTemplate = storedTemplates.find(
        (x) => x.id === template
      );
      const templateColumns = newlySelectedTemplate.columns;
      setTemplateName(newlySelectedTemplate.name);
      setSelectedColumns(
        templateColumns.filter((x) => columns.some((y) => y.value === x))
      );
    }
  };

  const changeExportType = (newExportType) => {
    setExportType(newExportType);
  };

  const onGenerateExport = () => {
    const cleanTemplateName = templateName.trim();
    if (saveTemplate && selectedTemplate === 'manual') {
      if (storedTemplates.some((x) => x.name === cleanTemplateName)) {
        setTemplateSubText('This template name already exists');
        return;
      }

      createTemplate({
        variables: {
          organizationId,
          name: cleanTemplateName,
          exportType: exportType,
          columns: selectedColumns,
        },
      }).then(() => {
        refetch({ organizationId });
      });
    }

    resetExportModal();
    onSubmit(
      columns.filter((x) => selectedColumns.includes(x.value)),
      exportType,
      cleanTemplateName
    );
  };

  const resetExportModal = () => {
    setSelectedTemplate('manual');
    setSelectedColumns([]);
    setTemplateName('');
    setSaveTemplate(false);
  };

  const onDeleteTemplate = () => {
    deleteTemplate({
      variables: {
        organizationId,
        id: +selectedTemplate,
      },
    }).then(() => {
      refetch({ organizationId });
      resetExportModal();
      reactiveFeedbackMessage({
        message: 'Template deleted',
        autoclose: true,
        isError: false,
      });
    });
  };

  const wrappedOnClose = () => {
    resetExportModal();
    onClose();
  };

  return (
    <ModalContainer
      disableOverlayClose={false}
      closeButton={true}
      isOpen={show}
      onClose={wrappedOnClose}
    >
      {/* Start Title */}
      <div id="export-modal-title">
        <h2 data-testid="modal-heading">{title}</h2>
      </div>
      {/* End Title */}

      <div
        data-testid="export-modal-content"
        id="export-modal-content"
        className="sui-mt-2 sui-pb-1"
      >
        {/* Start Export Type */}
        {exportTypes.length > 1 && (
          <Select
            name="exportType"
            label="Export Type"
            className="sui-mt-2 sui-pb-1"
            options={exportTypes}
            value={exportType}
            isMulti={false}
            onChange={changeExportType}
          />
        )}

        {/* End Export Type */}

        {/* Start Template */}
        <Select
          name="template"
          label="Template"
          className="sui-mt-2 sui-pb-1"
          value={selectedTemplate}
          options={[
            { label: 'Manually Select Columns', value: 'manual' },
            ...templates.map((template) => ({
              label: template.name,
              value: template.id,
            })),
          ]}
          onChange={onChangeTemplate}
        />
        {/* End Template */}

        {/* Start Columns */}
        <div className="sui-pb-1 sui-mt-2">
          <MultiSelectPicker
            values={selectedColumns}
            name="Columns"
            label="Columns to Include"
            className="sui-mt-2 sui-pb-1"
            onValuesChange={setSelectedColumns}
          >
            <MultiSelectPickerTrigger
              placeholder="Columns"
              className="sui-w-full"
              data-testid="columns-selector"
            />
            <MultiSelectPickerContent className="sui-z-[99999]">
              <MultiSelectPickerList>
                {columns.map((option) => (
                  <MultiSelectPickerItem
                    key={`MultiSelectPicker-option-column-${option.label}-${option.value}`}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </MultiSelectPickerList>
            </MultiSelectPickerContent>
          </MultiSelectPicker>
        </div>
        {/* End Columns */}
      </div>

      {/* Start Template Management */}
      <div
        className={`sui-mt-2 sui-pb-1 ${
          selectedTemplate !== 'manual' ? 'sui-hidden' : ''
        }`}
      >
        <Checkbox
          id="saveTemplate"
          name="saveTemplate"
          label="Save this selection as a template"
          className="sui-mb-2 sui-pb-1"
          value={`${saveTemplate}`}
          onChange={() => setSaveTemplate(!saveTemplate)}
        />

        <Input
          data-testid="template-name"
          type="text"
          value={templateName}
          label="Template Name"
          helpText={templateSubText}
          name="templateName"
          className={`sui-mt-2 sui-pb-1 ${saveTemplate ? '' : 'sui-hidden'}`}
          onChange={(e) => {
            setTemplateSubText(undefined);
            setTemplateName(e.target.value);
          }}
        />
      </div>

      <LabelButton
        labelText="Delete template"
        icon="delete"
        variantType="tertiary"
        size="default"
        sentiment="negative"
        iconPosition="left"
        className={`sui-flex sui-items-center ${
          selectedTemplate === 'manual' ? 'sui-hidden' : ''
        }`}
        onClick={onDeleteTemplate}
      />
      {/* End Template Management */}

      {/* Start Buttons */}
      <div className="sui-mt-2 sui-pb-1 sui-flex sui-justify-end">
        <LabelButton
          labelText="Cancel"
          variantType="secondary"
          onClick={wrappedOnClose}
          size="small"
          className="sui-mr-1"
        />
        <LabelButton
          labelText="Generate Export"
          variantType="primary"
          onClick={onGenerateExport}
          size="small"
          data-testid="generate-export-button"
          disabled={selectedColumns.length === 0}
        />
      </div>
      {/* End Button */}
    </ModalContainer>
  );
};

export default ExportModal;
